import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaWallet } from "react-icons/fa6";
import { useNavigate, useSearchParams } from "react-router-dom";
import { type DataProp, fetchBooking } from "services/fetch-booking";
import { fetchCardBin } from "services/fetch-subscription";
import type { Root as RootProfile } from "services/get-profile";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Formatter } from "utils/formatters";
import { isUserRestricted } from "utils/userRestricted";
import GuestsInputForm from "./GuestsInputForm";
import StayDatesRangeInputForm from "./StayDatesRangeInputForm";
import { FormItem } from "./components/form-item";
import { FormAdditionalPayment } from "./components/form-payment-addicional";
import type { BookingCondition } from "./components/type";
import type { Room } from "./type";

interface ReservationFormProps {
  data: BookingCondition | null;
  hotelId: number;
  room: Room | null;
  profile: RootProfile;
  cashback?: number;
}

export type OtherGuestsForm = {
  name: string;
  birth_date: string;
  cpf: string;
  isValidate: boolean;
};

export type PaymentFormAdditional = {
  card_number: string;
  card_holder: string;
  card_expiration: string;
  card_cvv: string;
  installments: number;
  values_installments: number[];
  use_credit_card_recurrence: boolean;
  use_other_credit_card: boolean;
  change_recurrent_card: boolean;
};

const dividesValueIntoInstallments = (value: number): number[] => {
  const installments = [];
  for (let i = 1; i <= 6; i++) {
    installments.push(value / i);
  }
  return installments;
};

export const ReservationForm = ({
  data,
  hotelId,
  room,
  profile,
  cashback,
}: ReservationFormProps) => {
  const [isMainGuest, setIsMainGuest] = useState<boolean>(true);
  const [submitBooking, setSubmitBooking] = useState<boolean>(false);
  const { loading, setLoading, setIsBooking } = useHotels();
  const [profileGuestMain, setProfileGuestMain] =
    useState<RootProfile>(profile);
  const [profileGuest, setProfileGuest] = useState({
    id: undefined,
    name: "",
    email: "",
    age: "",
    phone: "",
    cpf: "",
  });
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const numAdults = searchParams?.get("adults");
  const numChildren = searchParams?.get("children");
  const token = searchParams?.get("token");
  const [isValidate, setIsValidate] = useState<boolean>(false);
  const dataAtual = new Date();

  const totalPersons = Number(numAdults) + Number(numChildren);

  const ObjectFormTotalGuests: OtherGuestsForm[] = Array.from(
    { length: totalPersons - 1 },
    () => ({
      name: "",
      birth_date: "",
      cpf: "",
      isValidate: false,
    })
  );

  const [formTotalGuests, setFormTotalGuests] = useState<OtherGuestsForm[]>(
    ObjectFormTotalGuests
  );
  const [formAdditionalPayment, setFormAdditionalPayment] =
    useState<PaymentFormAdditional>({
      card_number: "",
      card_holder: "",
      card_expiration: "",
      card_cvv: "",
      installments: 1,
      values_installments: dividesValueIntoInstallments(1500),
      use_credit_card_recurrence: profile.wallet.is_card_token ? true : false,
      use_other_credit_card: profile.wallet.is_card_token ? false : true,
      change_recurrent_card: false,
    });

  const handleGuestChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (document.getElementById("reserve-container")) {
        document.getElementById("reserve-container")?.scrollIntoView({
          behavior: "smooth",
        });
      }
    }, 100);
    setIsMainGuest(event.target.id === "main");
  };

  const getBrand = async (number: string) => {
    const brand = await fetchCardBin(number);
    return brand;
  };

  const handleClickCreateBooking = async () => {
    if (!data || !token) {
      throw new Error("Data or token is not defined");
    }
    try {
      if (profile?.wallet.balance === 0) {
        toast.error("Você precisa de saldo na carteira para fazer uma reserva");
        return;
      }

      let creditCard;
      if (
        room?.price &&
        room.price.BRL > 0 &&
        formAdditionalPayment.use_other_credit_card
      ) {
        if (formAdditionalPayment.use_other_credit_card) {
          if (
            !formAdditionalPayment.card_number ||
            !formAdditionalPayment.card_holder ||
            !formAdditionalPayment.card_expiration ||
            !formAdditionalPayment.card_cvv
          ) {
            toast.error("Preencha todos os campos para pagamento adicional");
            return;
          }
        }

        const brand: any = await getBrand(
          formAdditionalPayment.card_number.split(" ").join("").substring(0, 6)
        );

        if (brand.response.status !== 200) {
          toast.error(brand.detail, { duration: 10000 });
          return;
        }

        creditCard = {
          card_number: formAdditionalPayment.card_number.split(" ").join(""),
          holder: formAdditionalPayment.card_holder,
          expiration_date: formAdditionalPayment.card_expiration,
          brand: brand.rest.provider,
          security_code: formAdditionalPayment.card_cvv,
        };
      }

      const isPaymentAdditional = room?.price && room.price.BRL > 0;

      let paymentAuthenticatedData = null;

      if (isPaymentAdditional) {
        paymentAuthenticatedData = {
          installments: formAdditionalPayment.installments,
          amount: room?.price.BRL,
          soft_descriptor: "Reserva",
          card: formAdditionalPayment.use_other_credit_card ? creditCard : null,
          card_credit_user_other: {
            use_other_credit_card: formAdditionalPayment.use_other_credit_card,
            change_recurrent_card: formAdditionalPayment.change_recurrent_card,
          },
        };
      }
      const schema = {
        hotel_id: hotelId,
        room_id: data.id,
        points: room?.price.points,
        token: token,
        guests: [] as unknown,
        BRL: isPaymentAdditional ? room?.price.BRL : 0,
        payment_authenticated: paymentAuthenticatedData
      };

      const profileGuestMainSchema = {
        name: profileGuestMain.name,
        cpf: profileGuestMain.cpf,
        birth_date: profileGuestMain.birth_date,
        phone: profileGuestMain.phone,
        email: profileGuestMain.email,
        main_pax: true,
      };

      const profileGuestSchema = {
        name: profileGuest.name,
        cpf: profileGuest.cpf,
        birth_date: profileGuest.age,
        phone: profileGuest.phone,
        email: profileGuest.email,
        main_pax: true,
      };

      if (isMainGuest) {
        schema.guests = [profileGuestMainSchema, ...formTotalGuests];
      } else {
        schema.guests = [profileGuestSchema, ...formTotalGuests];
      }

      setLoading(true);

      const response = await fetchBooking(schema as DataProp);

      if (!response) {
        setLoading(false);
        return 0;
      }

      setLoading(false);
      setSubmitBooking(true);
      const dataBooking = { ...response, cashback: cashback };
      navigate(`/booking-done/${response.id}`, { state: { dataBooking } });
    } catch (error) {
      setLoading(false);
      setSubmitBooking(false);

      if (error instanceof Error) {
        toast.error(error.message);
        return 0;
      }

      toast.error("Tivemos um erro ao criar a sua reserva");
    }
  };

  useEffect(() => {
    if (!data || !token) return;

    if (room?.price) {
      if (room.price.BRL > 0) {
        setFormAdditionalPayment({
          ...formAdditionalPayment,
          values_installments: dividesValueIntoInstallments(room.price.BRL),
        });
      }
    }

    const schema = {
      hotel_id: hotelId,
      room_id: data.id,
      points: data.price,
      token: token,
      guests: [
        {
          name: profileGuestMain.name,
          cpf: profileGuestMain.cpf,
          birth_date: profileGuestMain.birth_date,
          phone: profileGuestMain.phone,
          email: profileGuestMain.email,
          main_pax: true,
        },
        ...formTotalGuests,
      ],
    };

    const isValidateGuests = schema.guests.every((guest) => {
      return !!guest.name && !!guest.cpf && !!guest.birth_date;
    });

    // nome e sobrenome é obrigatório
    const isValidateGuestNameAndLastName = schema.guests.every((guest) => {
      return guest.name.trim().split(" ").length > 1;
    });

    let isValidateGuest = false;

    if (isMainGuest) {
      isValidateGuest =
        !!profileGuestMain.birth_date &&
        !!profileGuestMain.cpf &&
        !!profileGuestMain.name &&
        !!profileGuestMain.phone &&
        !!profileGuestMain.email &&
        profileGuestMain.name.trim().split(" ").length > 1;
    } else if (!isMainGuest) {
      isValidateGuest =
        !!profileGuest.age &&
        !!profileGuest.cpf &&
        !!profileGuest.name &&
        !!profileGuest.phone &&
        !!profileGuest.email &&
        profileGuest.name.trim().split(" ").length > 1;
    }

    const isValidate =
      isValidateGuests && isValidateGuest && isValidateGuestNameAndLastName;

    setIsValidate(isValidate);
  }, [
    data,
    hotelId,
    data?.price,
    profile,
    token,
    formTotalGuests,
    formTotalGuests.length,
    profileGuestMain,
    profileGuest,
  ]);

  if (!profile || !data || !profileGuestMain) {
    return (
      <div
        className="listingSection__wrap p-5 scroll-my-28 flex items-center justify-center"
        id="reserve-container"
      >
        Carregando...
      </div>
    );
  }

  const blockSubmitButton = submitBooking || loading || !isValidate;

  const renderRuleRefundable = (start_date: string, policyValue: number) => {
    if (!data?.is_non_refundable && new Date(start_date) > dataAtual) {
      return (
        <p>
          Cancelamento até{" "}
          {Formatter.formatterDateAndHour(start_date)?.formatedDate}
          &nbsp;às {
            Formatter.formatterDateAndHour(start_date)?.formateHour
          }{" "}
          após esta data será debitado na sua conta{" "}
          <span className="font-semibold">
            {new Intl.NumberFormat("pt-BR").format(policyValue)} ViroCash
          </span>
        </p>
      );
    }

    if (!data?.is_non_refundable && new Date(start_date) <= dataAtual) {
      return <span>{"Cancelamento irá gerar multa de ViroCash"}</span>;
    }

    return <span>Essa reserva não tem reembolso após reserva confirmada.</span>;
  };

  const isReservationButtonDisabled = isUserRestricted(profile.id!);

  return (
    <div
      className="listingSection__wrap p-5 scroll-my-28"
      id="reserve-container"
    >
      <div>
        <div className="">
          <h2 className="text-2xl font-semibold mb-4">Confirme sua reserva</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />
        </div>
        <div className="py-4">
          <h3 className="text-xl font-semibold mb-2">Dados da Reserva</h3>
          {/* <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div> */}
        </div>
        <form className="flex flex-col md:flex-row gap-2 md:gap-0 mb-4 justify-between mx-auto px-4 py-4 w-full border border-neutral-200 dark:border-neutral-700 rounded-3xl">
          <StayDatesRangeInputForm />
          <div className="md:w-[1px] w-full h-[1px] md:h-auto bg-neutral-200 dark:bg-neutral-700" />
          <GuestsInputForm />
        </form>
        <div className="py-4">
          <h3 className="text-xl font-semibold mb-2">Quarto</h3>
          {/* <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mb-4"></div> */}
          <p className="text-sm mb-2">{room?.description}</p>
          <p className="text-sm">{room?.board_description}</p>
        </div>
        <div className="w-full border-b border-neutral-200 dark:border-neutral-700" />

        <div className="flex justify-end gap-4 py-4 font-semibold mb-10">
          <FaWallet className="self-center w-6 h-6 md:w-8 md:h-8" />
          <span className="relative flex flex-col justify-end text-2xl md:text-3xl">
            {room && Formatter.prettyPriceHotel(room.price, profile)}
            {cashback ? (
              <span className="absolute -bottom-8 right-1 text-base whitespace-nowrap font-normal italic text-[#F97316]">
                Ganhe <strong>{`${cashback} ViroCash`}</strong> de volta!
              </span>
            ) : null}
          </span>
        </div>

        <h3 className="text-lg font-semibold mb-2">Política de Cancelamento</h3>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />
        <div className="grid grid-cols-1 gap-4 mt-4">
          {data?.cancellation_policies.map((policy) => {
            return renderRuleRefundable(policy.start_date, policy.value);
          })}
        </div>
      </div>
      <div>
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Hóspedes</h3>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />
          <div className="gap-4 my-4 text-sm flex items-center">
            <div>
              <input
                defaultChecked
                type="radio"
                name="guests"
                id="main"
                className="rounded-full w-3 h-3 border border-neutral-200 dark:border-neutral-700"
                onChange={handleGuestChange}
              />{" "}
              Eu sou o hóspede principal
            </div>
            <div>
              <input
                type="radio"
                name="guests"
                id="guests"
                className="rounded-full w-3 h-3 border border-neutral-200 dark:border-neutral-700"
                onChange={handleGuestChange}
              />{" "}
              Reservando para outra pessoa
            </div>
          </div>
          {isMainGuest && profile && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  Nome Completo
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  onChange={(e) => {
                    setProfileGuestMain({
                      ...profile,
                      name: e.target.value,
                    });
                  }}
                  value={profileGuestMain.name}
                  // defaultValue={profile.name}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={profileGuestMain.email}
                  onChange={(e) => {
                    setProfileGuestMain({
                      ...profile,
                      email: e.target.value,
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  Telefone
                </label>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  value={profileGuestMain.phone}
                  onChange={(e) => {
                    setProfileGuestMain({
                      ...profile,
                      phone: Formatter.prettyPhone(e.target.value),
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
              {/* age */}
              <div>
                <label
                  htmlFor="age"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  Data de Nascimento
                </label>
                {/* type date */}
                <input
                  type="date"
                  name="age"
                  id="age"
                  value={profileGuestMain.birth_date}
                  onChange={(e) => {
                    setProfileGuestMain({
                      ...profile,
                      birth_date: e.target.value,
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
              {/* cpf */}
              <div>
                <label
                  htmlFor="cpf"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  CPF
                </label>
                <input
                  type="text"
                  name="cpf"
                  id="cpf"
                  defaultValue={profileGuestMain.cpf}
                  onChange={(e) => {
                    setProfileGuestMain({
                      ...profile,
                      cpf: Formatter.prettyCPF(e.target.value),
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
            </div>
          )}

          {!isMainGuest && profileGuest && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  Nome Completo
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={profileGuest.name}
                  onChange={(e) => {
                    setProfileGuest({
                      ...profileGuest,
                      name: e.target.value,
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={profileGuest.email}
                  onChange={(e) => {
                    setProfileGuest({
                      ...profileGuest,
                      email: e.target.value,
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  Telefone
                </label>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  value={profileGuest.phone}
                  onChange={(e) => {
                    setProfileGuest({
                      ...profileGuest,
                      phone: Formatter.prettyPhone(e.target.value),
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
              {/* age */}
              <div>
                <label
                  htmlFor="age"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  Data de Nascimento
                </label>

                <input
                  type="date"
                  name="age"
                  id="age"
                  value={profileGuest.age}
                  onChange={(e) => {
                    setProfileGuest({
                      ...profileGuest,
                      age: e.target.value,
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
              {/* cpf */}
              <div>
                <label
                  htmlFor="cpf"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
                >
                  CPF
                </label>
                <input
                  type="text"
                  name="cpf"
                  id="cpf"
                  value={profileGuest.cpf}
                  onChange={(e) => {
                    setProfileGuest({
                      ...profileGuest,
                      cpf: Formatter.prettyCPF(e.target.value),
                    });
                  }}
                  className="w-full border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
                />
              </div>
            </div>
          )}

          {totalPersons > 1 && (
            <div className="mt-8">
              <div className="grid grid-cols-1 gap-4">
                {Array.from({ length: totalPersons - 1 }, (_, index) => (
                  <>
                    <h3
                      key={`guest-${index + 1}`}
                      className="text-lg font-semibold mb-2"
                    >
                      Hóspede {index + 1 + 1}
                    </h3>
                    <FormItem
                      value={formTotalGuests[index]}
                      set={(value) => {
                        const newFormTotalGuests = formTotalGuests;
                        newFormTotalGuests[index] = value;
                        setFormTotalGuests([...newFormTotalGuests]);
                      }}
                    />
                  </>
                ))}
              </div>
            </div>
          )}
          {/* Formulário de pagamento adicional */}
          {profile && room?.price && room.price.BRL > 0 && (
            <>
              <div className="w-full border-b border-neutral-200 dark:border-neutral-700 mt-8 mb-8" />
              <div className="flex flex-col gap-4 mb-8">
                <h3 className="text-lg font-semibold mb-2">
                  Pagamento Adicional
                </h3>
                <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />
                <div className="grid grid-cols-1 gap-4 mt-4">
                  <FormAdditionalPayment
                    value={formAdditionalPayment}
                    set={(value) => setFormAdditionalPayment(value)}
                  />
                </div>
              </div>
            </>
          )}
          <div
            className={`
						${
              room?.price && room.price.BRL > 0
                ? "mt-4 mb-4 max-h-12 h-full flex gap-4"
                : "mt-16 mb-4 max-h-12 h-full flex gap-4"
            }
					`}
          >
            <ButtonPrimary
              type="button"
              className="w-full bg-transparent border-2 text-neutral-500 border-neutral-400 hover:border-neutral-500 hover:text-neutral-600 transition-colors duration-300 ease-in-out"
              onClick={() => {
                setIsBooking(false);
                if (document.getElementById("topo")) {
                  document.getElementById("topo")?.scrollIntoView({
                    behavior: "smooth",
                  });
                }
              }}
            >
              Cancelar
            </ButtonPrimary>
            <ButtonPrimary
              type="submit"
              className="w-full py-3 bg-green-700 text-white hover:bg-green-700/80 transition-colors duration-300 ease-in-out disabled:bg-[#b2d0e0] disabled:cursor-not-allowed"
              onClick={handleClickCreateBooking}
              disabled={blockSubmitButton || isReservationButtonDisabled}
            >
              Confirme sua reserva
            </ButtonPrimary>
          </div>
        </div>
      </div>
    </div>
  );
};
